/**
 * Error Tracking Service
 * Supports multiple error tracking providers (Sentry, LogRocket, custom)
 */

import { maskSensitiveData, maskObject } from '../utils/dataMasking'
import { sanitizeErrorForLogging } from '../utils/sanitizedErrors'

class ErrorTracker {
  constructor() {
    this.providers = []
    this.isInitialized = false
    this.logrocketModule = null
  }

  /**
   * Load LogRocket module (optional dependency)
   */
  async loadLogRocket() {
    if (this.logrocketModule) return this.logrocketModule
    try {
      // Dynamic import - will fail gracefully if package not installed
      const logrocketPackage = 'logrocket'
      const module = await import(/* @vite-ignore */ logrocketPackage)
      this.logrocketModule = module
      return module
    } catch (error) {
      // LogRocket not installed - return null instead of throwing
      return null
    }
  }

  /**
   * Initialize error tracking
   */
  init(config = {}) {
    if (this.isInitialized) return

    const { logrocket, custom } = config

    // Initialize LogRocket if configured
    if (logrocket && logrocket.appId) {
      this.loadLogRocket().then((LogRocketModule) => {
        if (LogRocketModule) {
          LogRocketModule.init(logrocket.appId, {
            dom: {
              textSanitizer: true,
              inputSanitizer: true,
            },
          })
          this.providers.push('logrocket')
        }
      }).catch(() => {
        // LogRocket not available, continue without it
      })
    }

    // Custom error tracking
    if (custom && custom.onError) {
      this.customHandler = custom.onError
      this.providers.push('custom')
    }

    this.isInitialized = true
  }

  /**
   * Capture exception
   */
  async captureException(error, context = {}) {
    // Sanitize error and context
    const sanitizedError = sanitizeErrorForLogging(error, context)
    const sanitizedContext = maskObject(context, {
      patterns: ['all'],
    })

    // Get request ID if available
    const { getCurrentRequestId } = await import('../utils/requestTracking')
    const requestId = getCurrentRequestId() || context.requestId

    // Enhanced context capture
    const enhancedContext = {
      ...sanitizedContext,
      requestId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      referrer: document.referrer,
      language: navigator.language,
      online: navigator.onLine,
      cookieEnabled: navigator.cookieEnabled,
      platform: navigator.platform,
      // React context if available
      reactComponent: context.component || null,
      reactProps: context.props ? maskObject(context.props, { patterns: ['all'] }) : null,
      // Performance context
      performance: {
        memory: performance.memory ? {
          usedJSHeapSize: performance.memory.usedJSHeapSize,
          totalJSHeapSize: performance.memory.totalJSHeapSize,
          jsHeapSizeLimit: performance.memory.jsHeapSizeLimit,
        } : null,
        timing: performance.timing ? {
          domContentLoaded: performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart,
          loadComplete: performance.timing.loadEventEnd - performance.timing.navigationStart,
        } : null,
      },
    }

    const errorInfo = {
      message: sanitizedError.message,
      stack: sanitizedError.stack,
      context: enhancedContext,
      timestamp: sanitizedError.timestamp,
      userAgent: navigator.userAgent,
      url: window.location.href,
    }

    // Aggregate error
    const { aggregateError } = await import('./errorAggregation')
    const aggregation = aggregateError(error, enhancedContext)
    errorInfo.fingerprint = aggregation.fingerprint
    errorInfo.count = aggregation.count
    enhancedContext.fingerprint = aggregation.fingerprint

    // Check alerts
    const { checkAlert } = await import('./errorAlerting')
    const organizationId = context.organizationId || enhancedContext.organizationId
    const userId = context.userId || enhancedContext.userId
    await checkAlert(error, { ...enhancedContext, fingerprint: aggregation.fingerprint }, organizationId)

    // Sentry integration removed

    // Send to LogRocket (with sanitized data)
    if (this.providers.includes('logrocket')) {
      this.loadLogRocket().then((LogRocketModule) => {
        if (LogRocketModule) {
          LogRocketModule.captureException(error, {
            tags: sanitizedContext.tags || {},
          })
        }
      }).catch(() => {
        // LogRocket not available
      })
    }

    // Custom handler (with sanitized data)
    if (this.customHandler) {
      this.customHandler(error, sanitizedContext)
    }

    // Fallback: Store in localStorage for later retrieval
    try {
      const errors = JSON.parse(localStorage.getItem('error_logs') || '[]')
      errors.push(errorInfo)
      // Keep only last 50 errors
      if (errors.length > 50) {
        errors.shift()
      }
      localStorage.setItem('error_logs', JSON.stringify(errors))
    } catch (e) {
      console.error('Failed to log error:', e)
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('Error captured:', errorInfo)
    }

    return errorInfo
  }

  /**
   * Flush error aggregations
   */
  async flushAggregations(organizationId = null, userId = null) {
    const { flushAggregations } = await import('./errorAggregation')
    return flushAggregations(organizationId, userId)
  }

  /**
   * Get error statistics
   */
  async getErrorStatistics(organizationId, hours = 24) {
    try {
      const { supabase } = await import('./supabase')
      const { data, error } = await supabase.rpc('get_error_statistics', {
        org_id: organizationId,
        hours,
      })

      if (error) throw error
      return data?.[0] || null
    } catch (error) {
      console.error('Failed to get error statistics:', error)
      return null
    }
  }

  /**
   * Get unacknowledged alerts
   */
  async getUnacknowledgedAlerts(organizationId) {
    try {
      const { supabase } = await import('./supabase')
      const { data, error } = await supabase.rpc('get_unacknowledged_alerts', {
        org_id: organizationId,
      })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Failed to get unacknowledged alerts:', error)
      return []
    }
  }

  /**
   * Acknowledge alert
   */
  async acknowledgeAlert(alertId, userId) {
    try {
      const { supabase } = await import('./supabase')
      const { error } = await supabase.rpc('acknowledge_alert', {
        alert_id: alertId,
        user_id: userId,
      })

      if (error) throw error
      return true
    } catch (error) {
      console.error('Failed to acknowledge alert:', error)
      return false
    }
  }

  /**
   * Capture message
   */
  captureMessage(message, level = 'info', context = {}) {
    // Sanitize message and context
    const sanitizedMessage = maskSensitiveData(message, { patterns: ['all'] })
    const sanitizedContext = maskObject(context, { patterns: ['all'] })

    // Sentry integration removed

    // Send to LogRocket
    if (this.providers.includes('logrocket')) {
      this.loadLogRocket().then((LogRocketModule) => {
        if (LogRocketModule) {
          LogRocketModule.captureMessage(sanitizedMessage, {
            level,
            extra: sanitizedContext,
          })
        }
      }).catch(() => {
        // LogRocket not available
      })
    }

    // Custom handler
    if (this.customHandler) {
      this.customHandler({ message: sanitizedMessage, level, ...sanitizedContext }, sanitizedContext)
    }
  }

  /**
   * Set user context
   */
  setUser(user) {
    // Mask user data before sending
    const maskedUser = user ? {
      id: user.id,
      email: maskSensitiveData(user.email || '', { patterns: ['email'] }),
      username: maskSensitiveData(user.name || '', { patterns: ['all'], showFirst: 2, showLast: 0 }),
    } : null

    // Sentry integration removed

    // LogRocket
    if (this.providers.includes('logrocket')) {
      this.loadLogRocket().then((LogRocketModule) => {
        if (LogRocketModule && user) {
          LogRocketModule.identify(user.id, {
            email: maskSensitiveData(user.email || '', { patterns: ['email'] }),
            name: maskSensitiveData(user.name || '', { patterns: ['all'], showFirst: 2, showLast: 0 }),
          })
        }
      }).catch(() => {
        // LogRocket not available
      })
    }
  }

  /**
   * Set context/tags
   */
  setContext(key, value) {
    // Mask context value
    const maskedValue = typeof value === 'object' ? maskObject(value, { patterns: ['all'] }) : maskSensitiveData(String(value), { patterns: ['all'] })

    // Sentry integration removed

    // LogRocket
    if (this.providers.includes('logrocket')) {
      this.loadLogRocket().then((LogRocketModule) => {
        if (LogRocketModule) {
          LogRocketModule.setCustomProperty(key, maskedValue)
        }
      }).catch(() => {
        // LogRocket not available
      })
    }
  }

  /**
   * Breadcrumb tracking
   */
  addBreadcrumb(message, category = 'default', level = 'info', data = {}) {
    // Sanitize breadcrumb data
    const sanitizedMessage = maskSensitiveData(message, { patterns: ['all'] })
    const sanitizedData = maskObject(data, { patterns: ['all'] })

    // Sentry integration removed

    // LogRocket
    if (this.providers.includes('logrocket')) {
      this.loadLogRocket().then((LogRocketModule) => {
        if (LogRocketModule) {
          LogRocketModule.addAction(sanitizedMessage, {
            category,
            level,
            ...sanitizedData,
          })
        }
      }).catch(() => {
        // LogRocket not available
      })
    }
  }
}

// Singleton instance
const errorTracker = new ErrorTracker()

// Initialize with environment variables
if (import.meta.env.VITE_LOGROCKET_APP_ID) {
  errorTracker.init({
    logrocket: {
      appId: import.meta.env.VITE_LOGROCKET_APP_ID,
    },
  })
}

export default errorTracker

// Convenience exports
export const captureException = (error, context) => errorTracker.captureException(error, context)
export const captureMessage = (message, level, context) => errorTracker.captureMessage(message, level, context)
export const setUser = (user) => errorTracker.setUser(user)
export const setContext = (key, value) => errorTracker.setContext(key, value)
export const addBreadcrumb = (message, category, level, data) => errorTracker.addBreadcrumb(message, category, level, data)
export const flushAggregations = (organizationId, userId) => errorTracker.flushAggregations(organizationId, userId)
export const getErrorStatistics = (organizationId, hours) => errorTracker.getErrorStatistics(organizationId, hours)
export const getUnacknowledgedAlerts = (organizationId) => errorTracker.getUnacknowledgedAlerts(organizationId)
export const acknowledgeAlert = (alertId, userId) => errorTracker.acknowledgeAlert(alertId, userId)

